import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { Badge, Card, ErrorBox, Kpi, PageHeader, Spinner, Table, num } from "../components/ui.js";

interface IngestResult {
  batchId: number | null;
  filename: string;
  format: string;
  dryRun: boolean;
  total: number;
  inserted: number;
  updated: number;
  unchanged: number;
  skipped: number;
  duplicatesCollapsed: number;
  transportes: number;
  viagensCriadas: number;
  viagensAtualizadas: number;
  viagensVinculadas: number;
}

const PRIORIDADE_LABEL: Record<string, string> = { N: "Normal", P: "Prioridade", U: "Urgente" };

async function postFile(file: File, dryRun: boolean): Promise<IngestResult> {
  const buf = await file.arrayBuffer();
  const url = `/ingest/data?filename=${encodeURIComponent(file.name)}&dryRun=${dryRun}`;
  return (await api.post(url, buf, { headers: { "Content-Type": file.type || "application/octet-stream" } })).data;
}

function ImportPanel() {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<IngestResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const dryRun = useMutation({
    mutationFn: (f: File) => postFile(f, true),
    onSuccess: (r) => setPreview(r),
  });
  const confirm = useMutation({
    mutationFn: (f: File) => postFile(f, false),
    onSuccess: () => {
      setPreview(null);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      qc.invalidateQueries({ queryKey: ["programacao"] });
      qc.invalidateQueries({ queryKey: ["ingest-batches"] });
    },
  });

  const pick = (f: File | null) => {
    setFile(f);
    setPreview(null);
    dryRun.reset();
    confirm.reset();
    if (f) dryRun.mutate(f);
  };
  const result = confirm.data ?? preview;

  return (
    <Card className="mb-6">
      <h3 className="font-semibold mb-1">Importar arquivo DATA</h3>
      <p className="text-xs text-slate-400 mb-3">
        Anexe o export da programação (CSV ou XLSX). A importação é <b>idempotente</b>: linhas novas
        são inseridas, as que mudaram são atualizadas e as iguais são ignoradas — sem duplicar.
        Chave: nº de transporte + lote.
      </p>

      <div className="flex items-center gap-3 flex-wrap">
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          className="input max-w-sm"
          onChange={(e) => pick(e.target.files?.[0] ?? null)}
        />
        {file && (
          <button
            className="btn-primary"
            disabled={confirm.isPending || dryRun.isPending || !preview}
            onClick={() => confirm.mutate(file)}
          >
            {confirm.isPending ? "Importando…" : "Confirmar importação"}
          </button>
        )}
        {dryRun.isPending && <Spinner label="Analisando…" />}
      </div>

      {(dryRun.error || confirm.error) && (
        <div className="mt-3">
          <ErrorBox error={dryRun.error ?? confirm.error} />
        </div>
      )}

      {result && (
        <div className="mt-4">
          <div className="flex items-center gap-2 mb-2">
            <Badge>{confirm.data ? "Importado" : "Prévia (dry-run)"}</Badge>
            <span className="text-xs text-slate-400">
              {result.filename} · {result.format}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Kpi label="Inseridos" value={num(result.inserted)} />
            <Kpi label="Atualizados" value={num(result.updated)} />
            <Kpi label="Inalterados" value={num(result.unchanged)} />
            <Kpi label="Ignorados" value={num(result.skipped)} hint="sem nº / cabeçalho" />
            <Kpi
              label="Total"
              value={num(result.total)}
              hint={result.duplicatesCollapsed ? `${result.duplicatesCollapsed} dup. no arquivo` : undefined}
            />
          </div>
          {/* Ponte para Operações: a importação agora cria/atualiza a viagem
              editável. Antes o transporte ficava só neste ledger e nunca
              chegava à tela onde a torre vincula motorista. */}
          <div className="mt-3 rounded-lg bg-slate-50 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Reflexo em Operações
            </div>
            <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Kpi label="Transportes" value={num(result.transportes)} hint="linhas agrupadas por nº de transporte" />
              <Kpi label="Viagens criadas" value={num(result.viagensCriadas)} />
              <Kpi label="Viagens atualizadas" value={num(result.viagensAtualizadas)} />
              <Kpi label="Viagens vinculadas" value={num(result.viagensVinculadas)} hint="já existiam e ganharam vínculo" />
            </div>
            <p className="mt-2 text-xs text-slate-400">
              As viagens ficam editáveis em <b>Operações</b> (origem “Programação”) — dá para
              vincular motorista e agendar. Reimportar não sobrescreve o que o operador editou.
            </p>
          </div>

          {!confirm.data && (
            <p className="mt-2 text-xs text-slate-400">
              Prévia — nada foi gravado ainda. Clique em “Confirmar importação” para aplicar.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

function ApiSyncPanel() {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ["bntg-status"],
    queryFn: async () => (await api.get("/ingest/api/status")).data as {
      configured: boolean;
      sources: { source: string; generated_at: string; fetched_at: string; row_count: number }[];
    },
  });
  const sync = useMutation({
    mutationFn: async (dryRun: boolean) => (await api.post(`/ingest/api/sync?dryRun=${dryRun}`)).data as {
      dryRun: boolean; programming: IngestResult;
    },
    onSuccess: (result) => {
      if (!result.dryRun) qc.invalidateQueries();
    },
  });
  const r = sync.data?.programming;
  return <Card className="mb-6">
    <h3 className="font-semibold mb-1">Integração com as fontes</h3>
    <p className="text-sm text-slate-500 mb-3">
      Arcelor atualiza a programação e as viagens. Intercarg, Opentech e o cadastro de motoristas
      alimentam as consultas e as sugestões de vínculo. Confira as sugestões antes de salvar a carga.
    </p>
    {status.isLoading && <Spinner label="Consultando integração…" />}
    {status.error && <ErrorBox error={status.error} />}
    {status.data && !status.data.configured && <p className="mb-3 text-sm">Configure a credencial da integração no servidor.</p>}
    {status.data?.configured && status.data.sources.length === 0 && <p className="mb-3 text-sm">Nenhuma sincronização concluída.</p>}
    {status.data && status.data.sources.length > 0 && <Table columns={[
      { key: "source", label: "Fonte" },
      { key: "row_count", label: "Linhas recebidas", render: (v) => num(v as number) },
      { key: "generated_at", label: "Dados gerados em", render: (v) => new Date(String(v)).toLocaleString("pt-BR") },
      { key: "fetched_at", label: "Última sincronização", render: (v) => new Date(String(v)).toLocaleString("pt-BR") },
    ]} rows={status.data.sources} />}
    <div className="flex gap-3 flex-wrap mt-3">
      <button className="btn-secondary" disabled={!status.data?.configured || sync.isPending} onClick={() => sync.mutate(true)}>Simular atualização</button>
      <button className="btn-primary" disabled={!status.data?.configured || sync.isPending} onClick={() => sync.mutate(false)}>Sincronizar agora</button>
      {sync.isPending && <Spinner label="Consultando cargas e motoristas…" />}
    </div>
    {sync.error && <div className="mt-3"><ErrorBox error={sync.error} /></div>}
    {r && !sync.isPending && !sync.error && <div className="mt-3" role="status">
      <Badge>{sync.data?.dryRun ? "Simulação — nada gravado" : "Sincronização concluída"}</Badge>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
        <Kpi label="Registros novos" value={num(r.inserted)} />
        <Kpi label="Atualizados" value={num(r.updated)} />
        <Kpi label="Inalterados" value={num(r.unchanged)} />
        <Kpi label="Transportes" value={num(r.transportes)} />
      </div>
    </div>}
  </Card>;
}

function BatchHistory() {
  const batches = useQuery({
    queryKey: ["ingest-batches"],
    queryFn: async () => (await api.get("/ingest/batches")).data as { rows: Record<string, unknown>[] },
  });
  const rows = batches.data?.rows ?? [];
  if (batches.isLoading || !rows.length) return null;
  return (
    <Card className="mb-6">
      <h3 className="font-semibold mb-2">Histórico de importações</h3>
      <Table
        columns={[
          { key: "uploaded_at", label: "Quando", render: (v) => new Date(String(v)).toLocaleString("pt-BR") },
          { key: "filename", label: "Arquivo" },
          { key: "uploaded_by", label: "Por" },
          { key: "rows_inserted", label: "Inseridos", render: (v) => num(v as number) },
          { key: "rows_updated", label: "Atualizados", render: (v) => num(v as number) },
          { key: "rows_unchanged", label: "Inalterados", render: (v) => num(v as number) },
        ]}
        rows={rows}
      />
    </Card>
  );
}

export function Programacao() {
  const { canWrite, can } = useAuth();
  const [q, setQ] = useState("");
  const list = useQuery({
    queryKey: ["programacao", q],
    queryFn: async () =>
      (await api.get(`/entities/data_programacao?limit=200${q ? `&q=${encodeURIComponent(q)}` : ""}`)).data as {
        rows: Record<string, unknown>[];
        total: number;
      },
  });

  return (
    <div>
      <PageHeader title="Programação (DATA)" subtitle="Ledger mestre — busca por ordem/cliente/cidade" />

      {can("sistema") && <ApiSyncPanel />}
      {canWrite("data_programacao") && <details className="mb-6"><summary className="cursor-pointer mb-3">Importação manual de arquivo</summary><ImportPanel /></details>}
      {canWrite("data_programacao") && <BatchHistory />}

      <input
        className="input mb-4 max-w-md"
        placeholder="Buscar nº transporte, cliente, cidade…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {list.isLoading ? (
        <Spinner />
      ) : (
        <Table
          columns={[
            { key: "transporte", label: "Transporte" },
            { key: "data_programacao", label: "Data", render: (v) => (v ? String(v).slice(0, 10).split("-").reverse().join("/") : "—") },
            { key: "hora_programacao", label: "Hora", render: (v) => (v ? String(v).slice(0, 5) : "—") },
            { key: "veiculo", label: "Veículo" },
            { key: "nome_cliente", label: "Cliente" },
            { key: "cidade_cliente", label: "Cidade" },
            { key: "peso", label: "Peso (t)", render: (v) => num(v as number) },
            { key: "patio", label: "Pátio" },
            // Código da Arcelor em "Prioridade da carga". Semeia os marcadores
            // urgente/prioridade da carga em Operações (migration 029).
            { key: "prioridade", label: "Prioridade", render: (v) => PRIORIDADE_LABEL[String(v ?? "").trim().toUpperCase()] ?? (v ? String(v) : "—") },
          ]}
          rows={list.data?.rows ?? []}
        />
      )}
      <p className="mt-3 text-xs text-slate-400">{list.data?.total ?? 0} registro(s).</p>
    </div>
  );
}
